import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = ({ className = '' }) => {
  const navigate = useNavigate();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const navigationItems = [
    { name: 'Dashboard', path: '/vehicle-monitoring-dashboard', icon: 'Monitor' },
    { name: 'Fleet Status', path: '/fleet-status', icon: 'Truck' },
    { name: 'Alerts', path: '/alerts', icon: 'AlertTriangle' },
    { name: 'Reports', path: '/reports', icon: 'FileText' },
  ];

  const moreMenuItems = [
    { name: 'Settings', path: '/settings', icon: 'Settings' },
    { name: 'Help', path: '/help', icon: 'HelpCircle' },
    { name: 'Admin', path: '/admin', icon: 'Shield' },
  ];

  const handleNavigation = (path) => {
    window.location.href = path;
  };

  const toggleMoreMenu = () => {
    setIsMoreMenuOpen(!isMoreMenuOpen);
    setIsUserMenuOpen(false); // Close user menu if open
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
    setIsMoreMenuOpen(false); // Close more menu if open
  };

  const handleLogout = () => {
    try {
      // Clear all authentication data from localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('rememberLogin');
      localStorage.removeItem('loginBlock'); // Clear any login blocks as well
      
      // Close the user menu
      setIsUserMenuOpen(false);
      
      // Navigate to login screen
      navigate('/login-screen');
    } catch (error) {
      console.error('Error during logout:', error);
      // Even if there's an error, still redirect to login
      navigate('/login-screen');
    }
  };

  // Get user data for display
  const getUserData = () => {
    try {
      const userData = localStorage.getItem('userData');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  };

  const currentUser = getUserData();

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-card border-b border-border shadow-minimal ${className}`}>
      <div className="flex items-center justify-between h-16 px-6">
        {/* Logo Section */}
        <div className="flex items-center">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg overflow-hidden">
              <img 
                src="/assets/images/logoGuardia-1756232511900.png" 
                alt="Guardia Nacional - Justicia y Paz" 
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex flex-col">
              <h1 className="text-lg font-semibold text-foreground">Vehicle Scanner</h1>
              <span className="text-xs text-muted-foreground font-mono">Guardia Nacional</span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="hidden md:flex items-center space-x-1">
          {navigationItems?.map((item) => (
            <Button
              key={item?.name}
              variant="ghost"
              size="sm"
              onClick={() => handleNavigation(item?.path)}
              iconName={item?.icon}
              iconPosition="left"
              iconSize={16}
              className="text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth"
            >
              {item?.name}
            </Button>
          ))}
          
          {/* More Menu */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleMoreMenu}
              iconName="MoreHorizontal"
              iconSize={16}
              className="text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth"
            >
              More
            </Button>
            
            {isMoreMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-popover border border-border rounded-lg shadow-card z-50">
                <div className="py-2">
                  {moreMenuItems?.map((item) => (
                    <button
                      key={item?.name}
                      onClick={() => {
                        handleNavigation(item?.path);
                        setIsMoreMenuOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                    >
                      <Icon name={item?.icon} size={16} className="mr-3" />
                      {item?.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Mobile Menu Button */}
        <div className="md:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMoreMenu}
            iconName="Menu"
            iconSize={20}
            className="text-muted-foreground hover:text-foreground"
          />
        </div>

        {/* User Actions */}
        <div className="hidden md:flex items-center space-x-2">
          <Button
            variant="ghost"
            size="icon"
            iconName="Bell"
            iconSize={18}
            className="text-muted-foreground hover:text-foreground relative"
          >
            <span className="absolute top-1 right-1 w-2 h-2 bg-accent rounded-full animate-pulse-slow"></span>
          </Button>
          
          {/* User Menu with Logout */}
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleUserMenu}
              iconName="User"
              iconSize={18}
              className="text-muted-foreground hover:text-foreground"
            />
            
            {isUserMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-popover border border-border rounded-lg shadow-card z-50">
                <div className="py-2">
                  {/* User Info */}
                  {currentUser && (
                    <div className="px-4 py-3 border-b border-border">
                      <p className="text-sm font-medium text-popover-foreground">
                        {currentUser?.name || 'Usuario'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {currentUser?.email || 'usuario@vehiclescanner.mx'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Rol: {currentUser?.role || 'admin'}
                      </p>
                    </div>
                  )}
                  
                  {/* Menu Items */}
                  <div className="py-1">
                    <button
                      onClick={() => {
                        handleNavigation('/profile');
                        setIsUserMenuOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                    >
                      <Icon name="User" size={16} className="mr-3" />
                      Mi Perfil
                    </button>
                    
                    <button
                      onClick={() => {
                        handleNavigation('/settings');
                        setIsUserMenuOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                    >
                      <Icon name="Settings" size={16} className="mr-3" />
                      Configuración
                    </button>
                  </div>
                  
                  {/* Logout Button */}
                  <div className="border-t border-border pt-1">
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-destructive hover:bg-destructive/10 hover:text-destructive transition-smooth"
                    >
                      <Icon name="LogOut" size={16} className="mr-3" />
                      Cerrar Sesión
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Mobile Menu Dropdown */}
      {isMoreMenuOpen && (
        <div className="md:hidden bg-card border-t border-border">
          <div className="px-4 py-2 space-y-1">
            {[...navigationItems, ...moreMenuItems]?.map((item) => (
              <button
                key={item?.name}
                onClick={() => {
                  handleNavigation(item?.path);
                  setIsMoreMenuOpen(false);
                }}
                className="flex items-center w-full px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md transition-smooth"
              >
                <Icon name={item?.icon} size={16} className="mr-3" />
                {item?.name}
              </button>
            ))}
            
            {/* Mobile Logout */}
            <div className="border-t border-border pt-2 mt-2">
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-3 py-2 text-sm text-destructive hover:bg-destructive/10 rounded-md transition-smooth"
              >
                <Icon name="LogOut" size={16} className="mr-3" />
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;